import { useContext, useEffect, useState } from "react";
import Modal from "../../../../components/modalWindow/modal";
import { ReactComponent as accountCircle } from '../../../../icons/account_circle.svg';
import { apiContext } from "../../../../contexts/apiContext";
import { AssignEditorVerifierModalProps } from "./types/assignEditorVerifierModalProps";
import SelectInput from "../../../../components/shared/selectInput/selectInput";
import { Option, SelectInputProps } from "../../../../components/shared/selectInput/types/selectInputProps";
import ButtonField from "../../../../components/buttonField/buttonField";
import { classNameBtn } from "../../../../components/shared/button/types/buttonProps";
import { stylizationVerticalAlign } from "../../../../components/shared/stylization";
import { ButtonFieldProps } from "../../../../components/buttonField/types/buttonFieldProps";
import { assignEditor, assignVerifier, getDefaultUser, getUsersByRoles } from "../../../../services/processService/processService";
import AuthContext, { Roles } from "../../../../contexts/authContext";
import { notifyError, notifySuccess } from "../../../../components/snackbar/snackbar";
import Spinner from "../../../../components/spinner/spinner";
import { ClassNameSpinner } from "../../../../components/spinner/types/spinnerProps";
import { ObjectState } from "../../taskListView/types/tasklListViewProps";
import './assignEditorVerifierModal.scss';
import { ReactComponent as Close } from "../../../../icons/close.svg";
import ToggleButton from "../../../../components/toggleButton/toggleButton";
import { TableContext } from "../../../../contexts/tableContext";
import ViewContext from "../../../../contexts/viewContext";

const AssignEditorVerifierModal = (props: AssignEditorVerifierModalProps) => {
    const api = useContext(apiContext);
    const { reloadTable, setReloadTable } = useContext(TableContext);
    const { setViewType, viewTypes } = useContext(ViewContext);
    const { userRoles } = useContext(AuthContext);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectOptions, setSelectOptions] = useState<Option[]>([]);
    const [selectedOption, setSelectedOption] = useState<Option[]>([]);
    const [defaultUser, setDefaultUser] = useState<any>(undefined);
    const [users, setUsers] = useState<any[]>([]);
    const [showVerifierToggle, setShowVerifierToggle] = useState<boolean>(false);
    const [verifierToggleStatus, setVerifierToggleStatus] = useState<ObjectState>(ObjectState.ToBeCorrected);
    const [btnToggle, setBtnToggle] = useState<boolean>(true);
    
    // Toggle button
    useEffect(() => {
        if (props.assignEditorByVerifier) { setShowVerifierToggle(true); }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    
    const handleToggleClickToBeCorrected = () => {
        handleClick(ObjectState.ToBeCorrected);
    }

    const handleToggleClickNoGeom = () => {
        handleClick(ObjectState.NoGeometry);
    }

    const handleClick = (state: ObjectState) => {
        setBtnToggle(!btnToggle);
        setVerifierToggleStatus(state);
    }
    
    // User select & default/selected user
    useEffect(() => {
        Promise.all([
            getUsersByRoles(api.apiUrl + api.apiVersion, props.isEditorVersion)
            .then((res: any) => {
                setUsers(res);
                prepareOptions(res);                        
            }),
            getDefaultUser(api.apiUrl + api.apiVersion, props.rows, props.isEditorVersion, !props.isEditorVersion && userRoles.includes(Roles.PwaEditorExtended), props.teryt)
            .then((res: any) => {
                setDefaultUser(res);               
            })           
        ])
        .then(() => {
            setLoading(false);
        })
        .catch(() => {
            setLoading(false);
            notifyError({ message: `Próba pobrania listy ${props.isEditorVersion ? "edytorów" : "weryfikatorów"} zakończona niepowodzeniem` });
        })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const prepareOptions = (data: any) => {
        var usersSelect: Option[] = data.map((item: any) => {
            return {
                value: item.id,
                name: `${item.nazwA_PELNA} (${item.nazwa})`
            };
        });
        setSelectOptions(usersSelect.sort((a: any, b: any) => a.name.localeCompare(b.name, 'pl')));
    }

    const onChangeSelectAssignUser = (opt: Option[]) => {
        setSelectedOption(opt);
        if (opt.length > 0) {
            var userSelected = users.find(item => item.id === opt[0].value);       
            setDefaultUser(userSelected);
        } 
        else {
            setDefaultUser(undefined);
        }
    }

    const selectInputProps: SelectInputProps = {
        label: "Wpisz imię i nazwisko użytkownika",
        placeHolder: "Wpisz imię i nazwisko użytkownika lub wybierz z listy",
        hasInputTags: true,
        options: selectOptions,
        onlySingleTag: true,
        onChange: onChangeSelectAssignUser,
        pristine: true,
        selectedOptions: selectedOption,
        style: {
            paddingTop: "10px",
            width: "99%"
        }
    }   

    // Action buttons
    const getBtnProps = (title: string, className: string, onClick: React.MouseEventHandler<HTMLButtonElement>, disable: boolean): ButtonFieldProps => {
        return {
            BtnProps: {
                onClick: onClick,
                className: `${classNameBtn.Btn} ${className} body-txt-medium`,
                disabled: disable
            },
            placeHolder: title,
            placeHolderStyle: stylizationVerticalAlign
        };
    }
    
    const handleSaveAssignUserClick = () => {
        setLoading(true);
        if (props.isEditorVersion) {
            if (props.triggeredFromForm && props.saveFormComments) {
                props.saveFormComments()
                .then((res: any) => {
                    if (res) assignEd();
                })
                .catch(() => { saveError(); })  
            }
            else assignEd();      
        }
        else {
            if (props.triggeredFromForm && props.saveForm) {
                props.saveForm()
                .then((res: any) => {
                    if (res) assignVer();
                })
                .catch(() => { saveError(); })  
            }
            else assignVer();
        }
    }

    const assignEd = () => {
        assignEditor(api.apiUrl + api.apiVersion, defaultUser, props.rows, verifierToggleStatus)
        .then(() => { handleAssignmentSuccess(props.rows); })
        .catch(() => { handleAssignmentError(); })  
    }

    const assignVer = () => {
        assignVerifier(api.apiUrl + api.apiVersion, defaultUser, props.rows)
        .then(() => { handleAssignmentSuccess(props.rows); })
        .catch(() => { handleAssignmentError(); }) 
    }

    const handleAssignmentSuccess = (rows: any[]) => {
        notifySuccess({ message: `Przypisano ${props.isEditorVersion ? "edytora" : "weryfikatora"}: ${defaultUser.nazwA_PELNA}`});
        setReloadTable(!reloadTable);
        props.onClose();
        if (props.triggeredFromForm) setViewType(viewTypes.TaskList);
        if (props.onSuccess !== undefined)
        {
            let changedRows = rows.map(r => {return {...r, PRZYPISANY_UZYTKOWNIK: defaultUser.nazwa}});
            props.onSuccess(changedRows);
        }
    }

    const handleAssignmentError = () => {
        notifyError({ message: `Próba przypisania ${props.isEditorVersion ? "edytora" : "weryfikatora"} zakończona niepowodzeniem` });
        setLoading(false);
    }

    const saveError = () => {
        setLoading(false);
    }

    const handleRemoveDefaultUser = () => {
        setDefaultUser(undefined);
        setSelectedOption([]);
    }

    const disableSaveBtn = () => {
        return (defaultUser === '' || defaultUser === undefined || defaultUser.id === 0) && selectedOption.length === 0
    }

    return (
        <Modal
            title={`Dodaj ${props.isEditorVersion ? "Edytora" : "Weryfikatora" }`}
            titleIcon={accountCircle}
            iconClass='titleIcon'
            width={'700px'}
            height={showVerifierToggle ? '450px' : '350px'}
            onClose={props.onClose}> 
            {loading && <div><Spinner spinnerClass={ClassNameSpinner.Big}/></div>}
            {!loading && 
                <>
                    {showVerifierToggle &&
                        <ToggleButton
                            label="Wybierz status"
                            btnToggle={btnToggle}
                            setBtnToggle={setBtnToggle}
                            leftText={"Do poprawy"}
                            rightText={"Oczekuje na geometrię"}
                            onChangeLeft={handleToggleClickToBeCorrected}
                            onChangeRight={handleToggleClickNoGeom}
                            style={{ 
                                height: "51px",
                                marginTop: "10px",
                                marginBottom: "30px"
                            }}
                        />
                    }                   
                    <SelectInput {...selectInputProps} />
                    <div className="defaultUserWrapper">
                        <p className='body-txt-medium'>
                            {`Wybrany / Domyślny ${props.isEditorVersion ? "edytor" : "weryfikator"}:`} 
                        </p>
                        {defaultUser && defaultUser.id !== 0 &&
                            <div className="brown-tag single-tag">
                                <Close onClick={() => { handleRemoveDefaultUser() }} />
                                <span style={{paddingRight: "5px"}}>{ `${defaultUser?.nazwA_PELNA} (${defaultUser?.nazwa})` }</span>
                            </div>
                        }
                        {(!defaultUser || defaultUser.id === 0) &&
                            <span className='body-txt-medium' style={{fontStyle: "italic"}}>brak użytkownika domyślnego</span>
                        }
                    </div>
                    <div className="modalBtns" style={{paddingRight: "10px"}}>
                        <ButtonField {...getBtnProps("Anuluj", classNameBtn.LargeSecondary, () => { props.onClose() }, false)}></ButtonField>
                        <ButtonField {...getBtnProps("Zapisz zmiany", classNameBtn.Large, () => { handleSaveAssignUserClick() }, disableSaveBtn())}></ButtonField>
                    </div>
                </>
            }
        </Modal>
    )
}

export default AssignEditorVerifierModal;